import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslate} from "../../system/useTranslate";
import {useTeam} from "../../models/team/use-team";
import {useTask} from "../../models/task/use-task";
import {useNavigate, useParams} from "react-router-dom";
import Members from "./tabs/members";
import Identification from "./tabs/identification";
import Projects from "./tabs/projects";
import Messages from "./tabs/messages";
import Calendar from "./tabs/calendar";
import Note from "./tabs/note";
import {BreadCrumb} from "primereact/breadcrumb";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import {TabPanel, TabView} from "primereact/tabview";
import Icon from "../../system/components/icon";
import SelectableTabsButton, {useSelectableTabs} from "../components/selectable-tabs-button";
import {usePublishAiScope} from "../../system/ai-scope-context";
import {teamDisplayName} from "./team-display-name";

function Team() {
    const translate = useTranslate();
    const {id} = useParams();
    const teamApi = useTeam();
    const taskApi = useTask();
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(0);
    const [teamData, setTeamData] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tasksError, setTasksError] = useState(null);

    const tabs = useMemo(() => [
        {id: "team-identification", label: "team-identification", icon: "mi mi:fingerprint", view: Identification},
        {id: "members", label: "members", icon: "mi mi:groups", view: Members},
        {id: "projects", label: "projects", icon: "mi mi:list", view: Projects},
        {id: "message", label: "message", icon: "mi mi:chat_bubble", view: Messages},
        {id: "calendar", label: "calendar", icon: "mi mi:calendar_month", view: Calendar},
        {id: "note", label: "note", icon: "mi mi:sticky_note_2", view: Note},
    ], []);
    const {visibleTabs, selectedIds, toggleTab} = useSelectableTabs(tabs, `visible-tabs:team:${id}`, activeIndex, setActiveIndex);

    const load = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);
        setTasksError(null);
        try {
            const current = await teamApi.getTeam(id);
            if (!current) throw new Error("team.error.not-found");
            setTeamData(current);
            try {
                setTasks(await teamApi.getTeamTasks(id));
            } catch (requestError) {
                setTasks([]);
                setTasksError(requestError?.message || "team.calendar.error");
            }
        } catch (requestError) {
            setError(requestError?.message || "team.error.load");
        } finally {
            setLoading(false);
        }
        // Hook methods intentionally change identity with local request state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { load(); }, [load]);

    usePublishAiScope({
        activeTab: visibleTabs[activeIndex]?.id,
        entityType: "team",
        entityDocumentId: teamData?.documentId || id,
        selectedEntityIds: [teamData?.documentId || id].filter(Boolean),
    });

    const createTask = async (payload) => {
        const created = await taskApi.createTask(payload?.value || payload);
        if (created) setTasks((current) => [...current, created]);
        return created;
    };

    const updateTask = async (task, changes) => {
        const updated = await taskApi.updateTask(task?.documentId || task?.id, changes);
        if (updated) setTasks((current) => current.map((item) => (
            String(item.documentId || item.id) === String(updated.documentId || updated.id) ? updated : item
        )));
        return updated;
    };

    if (loading) return <div className="h-full flex align-items-center justify-content-center"><ProgressSpinner/></div>;
    if (error || !teamData) return <div className="h-full flex flex-column align-items-center justify-content-center gap-3 p-4">
        <Message severity="error" text={translate(error || "team.error.not-found")}/>
        <div className="flex gap-2">
            <Button outlined className="text-xs gap-2" icon="pi pi-refresh" label={translate("team.retry")} onClick={load}/>
            <Button text className="text-xs gap-2" icon="pi pi-arrow-right" label={translate("teams")} onClick={() => navigate("/teams")}/>
        </div>
    </div>;

    const displayName = teamDisplayName(teamData, translate);
    const breadcrumb = [
        {label: translate("teams"), command: () => navigate("/teams")},
        {label: displayName},
        {label: translate(visibleTabs[activeIndex]?.label || visibleTabs[0]?.label)},
    ];

    return <div className="h-full flex flex-column min-h-0">
        <BreadCrumb separatorIcon={<Icon icon="mi mi:chevron_left text-xs"/>} className="bg-transparent border-none text-xs" model={breadcrumb}/>
        <div className="flex-1 min-h-0 relative">
            <SelectableTabsButton tabs={tabs} selectedIds={selectedIds} onChange={toggleTab} className="absolute z-5" style={{top: ".45rem", left: ".75rem"}}/>
            <TabView lazy activeIndex={activeIndex} onTabChange={(event) => setActiveIndex(event.index)}
                panelContainerClassName="flex-1 min-h-0 p-0 border-noround" className="h-full flex flex-column">
                {visibleTabs.map((tab, tabIndex) => {
                    const TabContent = tab.view;
                    return <TabPanel key={tab.id} header={translate(tab.label)} leftIcon={<Icon icon={tab.icon} className="pl-2"/>} className="h-full text-xs">
                        {tabIndex === activeIndex && <div className="h-full min-h-0 overflow-hidden">
                            <TabContent teamData={teamData} tasks={tasks} createTask={createTask} updateTask={updateTask}
                                tasksError={tasksError} onChange={load} onArchived={() => navigate("/teams", {replace: true})}/>
                        </div>}
                    </TabPanel>;
                })}
            </TabView>
        </div>
    </div>;
}

export default Team;
